<template>
  <form @submit.prevent="onSubmit">
    <vl-grid mod-stacked>
      <vl-column v-if="newPublicationCheck && newPublicationCheck.usesCodeLists" width="7" width-m="9" with-s="12">
        <vl-alert
          v-vl-spacer:bottom.medium
          icon="warning"
          mod-warning
          :title="$t('publications.create.usesCodeLists.label')"
          :content="$t('publications.create.usesCodeLists.description')"
        >
        </vl-alert>
      </vl-column>
      <vl-column v-if="fileRequired" width="7" width-m="9" with-s="12">
        <PublicationDatasetfile :loading="saving" :progress="uploadProgress" @file-removed="onFileRemoved" />
      </vl-column>
      <vl-column width="12">
        <vl-title tag-name="h2">{{ $t('publications.create.metadata.title') }}</vl-title>
      </vl-column>
      <vl-column v-if="hasMultipleProducts" width="12">
        <vl-grid>
          <vl-column width="12">
            <vl-alert
              icon="warning"
              :title="$t('publications.create.metadata.multipleProducts')"
              content=""
              mod-naked
            />
          </vl-column>
        </vl-grid>
      </vl-column>
      <vl-column width="12">
        {{ $t('publications.create.metadata.description') }}
      </vl-column>
      <vl-column width="12">
        <vl-grid v-vl-spacer:top.medium>
          <vl-accordion-list>
            <PublicationMetadata />
          </vl-accordion-list>
        </vl-grid>
      </vl-column>
      <vl-column>
        <div class="vl-u-spacer"></div>
        <vl-action-group>
          <vl-button type="submit" mod-large :mod-loading="saving" :mod-disabled="saving">{{
            $t('publications.create.confirm.label')
          }}</vl-button>
          <vl-button
            type="button"
            mod-large
            mod-secondary
            :mod-loading="saving"
            :mod-disabled="saving"
            @click="cancel"
            >{{ $t('publications.create.cancel.label') }}</vl-button
          >
        </vl-action-group>
      </vl-column>
    </vl-grid>
  </form>
</template>
<script lang="ts" setup>
import { computed, onMounted } from 'vue';
import { useForm } from 'vee-validate';
import { createValidationSchema } from './create-publication-formdata';
import PublicationDatasetfile from '../publication-dataset-file/PublicationDatasetFile.vue';
import PublicationMetadata from '../publication-metadata/PublicationMetadata.vue';
import { NewPublicationCheck, ProductSpecification } from '@/api/frontoffice/clients';

const props = defineProps<{
  fileRequired?: boolean;
  newPublicationCheck?: NewPublicationCheck;
  specifications: Array<ProductSpecification>;
  saving?: boolean;
  uploadProgress?: number;
}>();

const emit = defineEmits(['cancel', 'submit']);

const validationSchema = createValidationSchema({
  fileRequired: props.fileRequired
});

const { handleSubmit, setFieldValue, values } = useForm({
  validationSchema
});

const hasMultipleProducts = computed(() => {
  return values.products && values.products.length > 1;
});

onMounted(() => {
  if (props.specifications && props.specifications.length > 0) {
    updateProducts(props.specifications);
  }
});

const updateProducts = (specificationsForDelivery: ProductSpecification[]) => {
  const now = new Date();
  const date = new Date(now && now.setHours(0, 0, 0, 0));
  const products = specificationsForDelivery.map((specification) => {
    if (specification.skipMetadataPublication) {
      return {
        isFirstTime: false,
        versionDate: null,
        versionDescription: null,
        temporalLimitationFrom: null,
        temporalLimitationTo: null,
        specification
      };
    } else {
      return {
        isFirstTime: false,
        versionDate: date,
        versionDescription: null,
        temporalLimitationFrom: date,
        temporalLimitationTo: null,
        specification
      };
    }
  });
  setFieldValue('products', products);
};

const onFileRemoved = () => {
  setFieldValue('file', null, false);
};

const cancel = () => {
  emit('cancel');
};

const onSuccess = async (values) => {
  emit('submit', values);
};

const onInvalidSubmit = ({ values, errors, results }) => {
  // console.log(values); // current form values
  // console.log(errors); // a map of field names and their first error message
  // console.log(results); // a detailed map of field names and their validation results
};

const onSubmit = handleSubmit(onSuccess, onInvalidSubmit);
</script>

<style lang="scss" scoped></style>
